import each from 'lodash/each';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import some from 'lodash/some';

import TreeNode from 'src/presentation/components/pages/ivr/components/ivrTree/models/TreeNode';
import { PathElbowRadius, NodeSide } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { Point, IvrLink, IvrPath } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import {
  lineWith2Elbow,
  getMinPath,
  lineWith1Elbow,
  getSvgLine,
  isPathCrossNodes,
  firstAndLastLineInPathIsNotToSmall,
  lineStraight,
} from './pathHelper';

const toFlatNodesMap = nodes =>
  reduce(
    nodes,
    (acc, node) => {
      acc[node.id] = node;
      return acc;
    },
    {}
  );

export const generateLinks = (nodes: TreeNode[], ivrConfig) => {
  const flatNodesMap = toFlatNodesMap(nodes);

  const links = reduce(
    Object.keys(ivrConfig.nodesHierarchy),
    (acc: any, startPoint: string) => {
      const startId = startPoint;
      const targets = get(ivrConfig.nodesHierarchy, startPoint);

      const linksToTarget = reduce(
        targets,
        (linksToTarget: any, { id, option, optionIndex }) => {
          const startNode = flatNodesMap[startId];
          const endNode = flatNodesMap[id];

          if (startNode && endNode) {
            const isOptionExists = some(
              linksToTarget,
              (item: any) => item.target.id === id && item.source.id === startId && option === item.option
            );

            if (!isOptionExists) {
              linksToTarget.push({ source: startNode, target: flatNodesMap[id], option, optionIndex });
            }
          }

          return linksToTarget;
        },
        []
      );

      return [...acc, ...linksToTarget];
    },
    []
  );
  const linkPaths = calculateLinksPaths(links, nodes);

  const linksMapByTargetConnectingPoint = reduce(
    linkPaths,
    (acc, path: IvrPath) => {
      if (isEmpty(acc[path.target.id])) {
        acc[path.target.id] = {};
      }

      if (isEmpty(acc[path.target.id][path.targetPointSide])) {
        acc[path.target.id][path.targetPointSide] = [];
      }

      acc[path.target.id][path.targetPointSide].push({
        sourceId: get(path, 'source.id'),
        points: path.points,
        sourcePointSide: path.sourcePointSide,
        targetPointSide: path.targetPointSide,
        option: path.option,
        optionIndex: path.optionIndex,
      });

      return acc;
    },
    {}
  );

  each(
    Object.keys(linksMapByTargetConnectingPoint),
    nodeId => (flatNodesMap[nodeId].connectionPoints = linksMapByTargetConnectingPoint[nodeId])
  );
  return calculateLinksPaths(links, nodes);
};

export const getAllNodeLinks = (links: IvrLink[], node?: TreeNode) =>
  node
    ? filter(links, link => link.source.id === node.id || link.target.id === node.id || link.source.ref === node.id)
    : links;

export const calculateLinksPaths = (links, nodes, node?: TreeNode) =>
  getAllNodeLinks(links, node).map((link: IvrLink) => ({ ...link, ...getLinePoints(link, nodes) }));

const getLinePoints = ({ source, target, option, optionIndex }: IvrLink, nodes: TreeNode[]) => {
  let points: Point[];
  let sourcePointSide: NodeSide;
  let targetPointSide: NodeSide;

  const straightLine = lineStraight(source, target, option, optionIndex);
  if (
    !straightLine ||
    isPathCrossNodes(straightLine[0], nodes) ||
    !firstAndLastLineInPathIsNotToSmall(straightLine[0])
  ) {
    [points, sourcePointSide, targetPointSide] = lineWith1Elbow(source, target, option, optionIndex);
    if (isPathCrossNodes(points, nodes) || !firstAndLastLineInPathIsNotToSmall(points)) {
      [points, sourcePointSide, targetPointSide] = lineWith2Elbow(source, target);

      if (isPathCrossNodes(points, nodes) || !firstAndLastLineInPathIsNotToSmall(points)) {
        [points, sourcePointSide, targetPointSide] = getMinPath(source, target, option, optionIndex, nodes);
      }
    }
  } else {
    [points, sourcePointSide, targetPointSide] = straightLine;
  }

  const svgLine = getSvgLine(points, PathElbowRadius, true);

  return {
    showArrow: true,
    showStartPoint: true,
    svgLine,
    points,
    sourcePointSide,
    targetPointSide,
  };
};
